import { useState, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { useNotify, useTranslate } from 'react-admin';
import { formatCurrency, formatDate, formatNumber } from '../utils/reportHelpers';

export interface UseReportExportResult {
  exporting: boolean;
  /**
   * Returns true on successful export, false on failure. The hook emits a
   * react-admin toast (success or error) on every call so the user always
   * gets feedback — earlier versions silently swallowed errors into a
   * console log, which produced confusing "click did nothing" reports.
   */
  exportToCSV: (data: any[], filename: string, columns?: ColumnConfig[]) => boolean;
  /**
   * Returns true on successful export, false on failure. Same toast
   * contract as exportToCSV.
   */
  exportToExcel: (data: any[], filename: string, options: ExcelExportOptions) => Promise<boolean>;
  error: Error | null;
}

export interface ColumnConfig {
  key: string;
  label: string;
  format?: 'currency' | 'number' | 'date' | 'percentage' | ((value: any) => string);
}

export interface FilterInfo {
  startDate?: string;
  endDate?: string;
  site?: { id: string; name: string };
  vendor?: { id: string; name: string };
  charger?: { id: string; name: string };
  [key: string]: any;
}

export interface ExcelExportOptions {
  columns: ColumnConfig[];
  summary?: {
    title: string;
    data: Array<{ label: string; value: string | number }>;
    // Optional explanatory note rendered (wrapped) beneath the KPI rows on the
    // summary sheet — e.g. a caveat about rounding variance.
    note?: string;
    // Optional labelled sub-sections (title + label/value rows) rendered below
    // the KPIs/note — e.g. an energy-by-charging-window breakdown.
    sections?: Array<{ title: string; rows: Array<{ label: string; value: string | number }> }>;
  };
  // Optional totals row appended to the data sheet, keyed by column key. Values
  // are formatted per the matching column's `format`; non-numeric values (e.g.
  // a "Total" label in the first column) render as text. Bold + top border.
  totalsRow?: Record<string, any>;
  filters?: FilterInfo;
  translate?: (key: string, options?: any) => string;
  sheetName?: string;
  includeTimestamp?: boolean;
}

/**
 * Hook for exporting report data to various formats
 *
 * @example
 * const { exportToCSV, exporting } = useReportExport();
 *
 * const handleExport = () => {
 *   exportToCSV(reportData, 'revenue-report', [
 *     { key: 'date', label: 'Date', format: 'date' },
 *     { key: 'revenue', label: 'Revenue', format: 'currency' },
 *     { key: 'sessions', label: 'Sessions', format: 'number' }
 *   ]);
 * };
 */
export const useReportExport = (): UseReportExportResult => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const notify = useNotify();
  const translate = useTranslate();

  const formatValue = useCallback((value: any, format?: ColumnConfig['format']): string => {
    if (value === null || value === undefined) return '';

    if (typeof format === 'function') {
      return format(value);
    }

    switch (format) {
      case 'currency':
        return formatCurrency(Number(value));
      case 'number':
        return formatNumber(Number(value), 2);
      case 'date':
        return formatDate(value);
      case 'percentage':
        return `${formatNumber(Number(value), 2)}%`;
      default:
        return String(value);
    }
  }, []);

  const exportToCSV = useCallback((
    data: any[],
    filename: string,
    columns?: ColumnConfig[]
  ): boolean => {
    setExporting(true);
    setError(null);

    try {
      if (!data || data.length === 0) {
        throw new Error('No data to export');
      }

      // Determine columns from data if not provided
      const cols: ColumnConfig[] = columns || Object.keys(data[0]).map(key => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      }));

      // Build CSV content
      const headers = cols.map(col => `"${col.label}"`).join(',');
      const rows = data.map(row => {
        return cols.map(col => {
          const value = row[col.key];
          const formatted = formatValue(value, col.format);
          // Escape quotes and wrap in quotes
          return `"${String(formatted).replace(/"/g, '""')}"`;
        }).join(',');
      });

      const csvContent = [headers, ...rows].join('\n');

      // Add UTF-8 BOM for proper Chinese character display in Excel
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
      setExporting(false);
      notify(
        translate('reports.export.csvSuccess', { fallback: 'CSV exported successfully' }),
        { type: 'success' },
      );
      return true;
    } catch (err: any) {
      console.error('Export error:', err);
      setError(err);
      setExporting(false);
      // Distinguish "no rows" from actual failure so the user knows whether
      // to widen filters or report a bug.
      const isEmpty = err?.message === 'No data to export';
      notify(
        isEmpty
          ? translate('reports.export.noData', { fallback: 'No data to export — adjust filters and try again.' })
          : translate('reports.export.csvFailed', { fallback: 'CSV export failed — see console for details.' }),
        { type: isEmpty ? 'warning' : 'error' },
      );
      return false;
    }
  }, [formatValue, notify, translate]);

  const exportToExcel = useCallback(async (
    data: any[],
    filename: string,
    options: ExcelExportOptions
  ): Promise<boolean> => {
    setExporting(true);
    setError(null);

    try {
      if (!data || data.length === 0) {
        throw new Error('No data to export');
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'EV Charging Admin';
      workbook.created = new Date();

      // Brand colors (ARGB format: FF + RGB hex)
      const primaryColor = 'FF009A75'; // Green
      const headerBg = 'FFF5F5F5'; // Light grey
      const textDark = 'FF1A1A1A'; // Dark text
      const textGrey = 'FF606060'; // Grey text
      const borderColor = 'FFE0E0E0'; // Border
      const zebraStripe = 'FFFAFAFA'; // Zebra stripe
      const footerGrey = 'FF999999'; // Footer text

      // Add Summary Sheet (if summary data provided)
      if (options.summary) {
        const summarySheetName = options.translate
          ? options.translate('reports.summary', { fallback: 'Summary' })
          : 'Summary';
        const summarySheet = workbook.addWorksheet(summarySheetName);

        // Title
        summarySheet.mergeCells('A1:B1');
        const titleCell = summarySheet.getCell('A1');
        titleCell.value = options.summary.title;
        titleCell.font = { size: 16, bold: true };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        summarySheet.getRow(1).height = 30;

        // Summary data (KPI cards)
        let rowIndex = 3;
        options.summary.data.forEach(item => {
          const labelCell = summarySheet.getCell(`A${rowIndex}`);
          const valueCell = summarySheet.getCell(`B${rowIndex}`);

          labelCell.value = item.label;
          labelCell.font = { bold: true };

          valueCell.value = item.value;
          valueCell.font = { size: 14, bold: true };

          rowIndex++;
        });

        // Optional explanatory note (e.g. rounding-variance caveat), wrapped
        // across the merged A:B columns beneath the KPI rows.
        if (options.summary.note) {
          rowIndex += 1; // spacing
          summarySheet.mergeCells(`A${rowIndex}:B${rowIndex}`);
          const noteCell = summarySheet.getCell(`A${rowIndex}`);
          noteCell.value = options.summary.note;
          noteCell.font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
          noteCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
          summarySheet.getRow(rowIndex).height = 48;
          rowIndex++;
        }

        // Optional labelled sub-sections (e.g. energy-by-charging-window).
        if (options.summary.sections && options.summary.sections.length > 0) {
          options.summary.sections.forEach(section => {
            rowIndex += 2; // spacing before each section
            const sectionTitleCell = summarySheet.getCell(`A${rowIndex}`);
            sectionTitleCell.value = section.title;
            sectionTitleCell.font = { size: 14, bold: true };
            rowIndex += 1;
            section.rows.forEach(r => {
              const labelCell = summarySheet.getCell(`A${rowIndex}`);
              const valueCell = summarySheet.getCell(`B${rowIndex}`);
              labelCell.value = r.label;
              labelCell.font = { bold: true };
              valueCell.value = r.value;
              rowIndex++;
            });
          });
        }

        // Add filter conditions section if filters provided
        if (options.filters) {
          rowIndex += 2; // Add spacing

          // Section title
          const filterTitleCell = summarySheet.getCell(`A${rowIndex}`);
          filterTitleCell.value = options.translate
            ? options.translate('reports.filters.title', { fallback: 'Filter Conditions' })
            : 'Filter Conditions';
          filterTitleCell.font = { size: 14, bold: true };
          rowIndex += 2;

          // Date range (always show)
          if (options.filters.startDate && options.filters.endDate) {
            const dateLabel = options.translate
              ? options.translate('reports.filters.dateRange', { fallback: 'Date Range' })
              : 'Date Range';
            const dateLabelCell = summarySheet.getCell(`A${rowIndex}`);
            const dateValueCell = summarySheet.getCell(`B${rowIndex}`);

            dateLabelCell.value = dateLabel;
            dateLabelCell.font = { bold: true };
            dateValueCell.value = `${options.filters.startDate} - ${options.filters.endDate}`;
            rowIndex++;
          }

          // Site (only if specific site selected)
          if (options.filters.site?.name) {
            const siteLabel = options.translate
              ? options.translate('reports.filters.site', { fallback: 'Site' })
              : 'Site';
            const siteLabelCell = summarySheet.getCell(`A${rowIndex}`);
            const siteValueCell = summarySheet.getCell(`B${rowIndex}`);

            siteLabelCell.value = siteLabel;
            siteLabelCell.font = { bold: true };
            siteValueCell.value = options.filters.site.name;
            rowIndex++;
          }

          // Vendor (only if specific vendor selected)
          if (options.filters.vendor?.name) {
            const vendorLabel = options.translate
              ? options.translate('reports.filters.vendor', { fallback: 'Vendor' })
              : 'Vendor';
            const vendorLabelCell = summarySheet.getCell(`A${rowIndex}`);
            const vendorValueCell = summarySheet.getCell(`B${rowIndex}`);

            vendorLabelCell.value = vendorLabel;
            vendorLabelCell.font = { bold: true };
            vendorValueCell.value = options.filters.vendor.name;
            rowIndex++;
          }

          // Charger (only if specific charger selected)
          if (options.filters.charger?.name) {
            const chargerLabel = options.translate
              ? options.translate('reports.filters.charger', { fallback: 'Charger' })
              : 'Charger';
            const chargerLabelCell = summarySheet.getCell(`A${rowIndex}`);
            const chargerValueCell = summarySheet.getCell(`B${rowIndex}`);

            chargerLabelCell.value = chargerLabel;
            chargerLabelCell.font = { bold: true };
            chargerValueCell.value = options.filters.charger.name;
            rowIndex++;
          }
        }

        // Column widths
        summarySheet.getColumn(1).width = 30;
        summarySheet.getColumn(2).width = 20;
      }

      // Add Data Sheet
      const dataSheetName = options.sheetName || (options.translate
        ? options.translate('reports.tabs.dataTable', { fallback: 'Data' })
        : 'Data');
      const dataSheet = workbook.addWorksheet(dataSheetName);

      // Add headers
      const headerRow = dataSheet.getRow(1);
      options.columns.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = col.label;
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: headerBg }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
      headerRow.height = 25;

      // Freeze header row
      dataSheet.views = [
        { state: 'frozen', ySplit: 1 }
      ];

      // Add data rows
      data.forEach((row, rowIndex) => {
        const excelRow = dataSheet.getRow(rowIndex + 2);

        options.columns.forEach((col, colIndex) => {
          const cell = excelRow.getCell(colIndex + 1);
          const value = row[col.key];

          // Set raw value
          if (value !== null && value !== undefined) {
            // For dates, use Date object
            if (col.format === 'date' && value) {
              cell.value = new Date(value);
              cell.numFmt = 'yyyy-mm-dd';
            } else if (typeof col.format === 'function') {
              cell.value = col.format(value);
            } else {
              cell.value = value;

              // Apply number formatting
              if (typeof col.format === 'string') {
                switch (col.format) {
                  case 'currency':
                    cell.numFmt = '"HK$"#,##0.00';
                    break;
                  case 'number':
                    cell.numFmt = '#,##0.00';
                    break;
                  case 'percentage':
                    cell.numFmt = '0.00%';
                    // Divide by 100 if value is already in percentage form (e.g., 75 for 75%)
                    if (typeof value === 'number' && value > 1) {
                      cell.value = value / 100;
                    }
                    break;
                }
              }
            }
          }

          // Alignment
          if (col.format === 'currency' || col.format === 'number' || col.format === 'percentage') {
            cell.alignment = { horizontal: 'right' };
          }
        });
      });

      // Optional totals row — bold, with a top border, appended after the data.
      if (options.totalsRow) {
        const totalsExcelRow = dataSheet.getRow(data.length + 2);
        options.columns.forEach((col, colIndex) => {
          const cell = totalsExcelRow.getCell(colIndex + 1);
          const value = options.totalsRow![col.key];
          if (value === null || value === undefined) {
            // leave blank
          } else if (typeof value === 'number' && (col.format === 'currency' || col.format === 'number' || col.format === 'percentage')) {
            cell.value = col.format === 'percentage' && value > 1 ? value / 100 : value;
            cell.numFmt =
              col.format === 'currency' ? '"HK$"#,##0.00'
              : col.format === 'percentage' ? '0.00%'
              : '#,##0.00';
          } else {
            // Non-numeric (e.g. the "Total" label in the first column).
            cell.value = String(value);
          }
          cell.font = { bold: true };
          cell.border = { top: { style: 'thin', color: { argb: 'FFBFBFBF' } } };
          if (col.format === 'currency' || col.format === 'number' || col.format === 'percentage') {
            cell.alignment = { horizontal: 'right' };
          }
        });
      }

      // Auto-fit columns
      options.columns.forEach((col, index) => {
        const column = dataSheet.getColumn(index + 1);
        let maxLength = col.label.length;

        data.forEach(row => {
          const value = row[col.key];
          if (value !== null && value !== undefined) {
            const formattedValue = formatValue(value, col.format);
            const length = String(formattedValue).length;
            if (length > maxLength) {
              maxLength = length;
            }
          }
        });

        // Set width with some padding
        column.width = Math.min(Math.max(maxLength + 2, 12), 50);
      });

      // Add timestamp footer if requested
      if (options.includeTimestamp) {
        const footerRow = dataSheet.getRow(data.length + 3);
        const generatedLabel = options.translate
          ? options.translate('reports.excel.generated', { fallback: 'Generated' })
          : 'Generated';
        footerRow.getCell(1).value = `${generatedLabel}: ${new Date().toLocaleString()}`;
        footerRow.getCell(1).font = { italic: true, size: 9 };
      }

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}-${timestamp}.xlsx`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
      setExporting(false);
      notify(
        translate('reports.export.excelSuccess', { fallback: 'Excel exported successfully' }),
        { type: 'success' },
      );
      return true;
    } catch (err: any) {
      console.error('Excel export error:', err);
      setError(err);
      setExporting(false);
      const isEmpty = err?.message === 'No data to export';
      notify(
        isEmpty
          ? translate('reports.export.noData', { fallback: 'No data to export — adjust filters and try again.' })
          : translate('reports.export.excelFailed', { fallback: 'Excel export failed — see console for details.' }),
        { type: isEmpty ? 'warning' : 'error' },
      );
      return false;
    }
  }, [formatValue, notify, translate]);

  return {
    exporting,
    exportToCSV,
    exportToExcel,
    error,
  };
};
